import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { defineMessages } from 'react-intl';
import type { UseQueryResult } from '@tanstack/react-query';
import { PageUrl } from '../../../../pages/utils/page-url';
import { useSLTConfig } from '../../../../slt/config/SLTConfigProvider';
import { QueryKey, rawUrlHash, useCustomPreferenceOrDefault } from '../../../../utils/api';
import { CustomPreference } from '../../../../utils/api/CustomPreference';
import { getLinksUrls } from '../utils/getLinksUrls';
import { links as linksToRetrieve } from './links';
import type { FooterLink, LinkGroupProps, ResolvedFooterLink } from '../types';

export function useExperienceLinks(): UseQueryResult<LinkGroupProps> {
    const { urlMap } = useSLTConfig();
    const enableRioSEO = useCustomPreferenceOrDefault(CustomPreference.ENABLE_RIO_SEO, true);

    // TODO: Remove this once the PWA Gift Cards is in production
    // https://dbuy-fi.atlassian.net/browse/SLTBE-5613
    const pwaGiftCardsEnabled = useCustomPreferenceOrDefault(
        CustomPreference.PWA_ENABLE_GIFT_CARDS,
        true,
    );
    const allUrlsFound = useMemo(
        () => linksToRetrieve.every((link) => rawUrlHash(link.url) in urlMap),
        [urlMap],
    );
    const updateFooterLinks = useMemo(
        () =>
            footerLinks.map((fl) => ({
                ...fl,
                ...(enableRioSEO && fl.id === 2 ? { url: PageUrl.STORES } : {}),
            })),
        [enableRioSEO],
    );

    return useQuery({
        queryKey: [QueryKey.EXPERIENCE_LINKS],
        initialData: {
            title: messages.experienceTitle,
            containerDataTestId: 'footer-experience-links-container',
            headerDataTestId: 'footer-experience-links-header',
            links: updateFooterLinks.map((fl) => {
                const linkToRetrieve = linksToRetrieve.find((ltr) => ltr.id === fl.id);

                // TODO: Remove this once the PWA Gift Cards is in production
                // https://dbuy-fi.atlassian.net/browse/SLTBE-5613
                if (!!pwaGiftCardsEnabled && fl.id === 5) {
                    return {
                        ...fl,
                        url: PageUrl.GIFT_CARDS,
                        isPwaLink: true,
                    };
                }

                return {
                    ...fl,
                    url:
                        fl.url ??
                        (linkToRetrieve?.url
                            ? (urlMap[rawUrlHash(linkToRetrieve?.url)] ?? '')
                            : ''),
                    isPwaLink: false,
                };
            }),
        },
        queryFn: async () => {
            const resolvedLinksMap = await getLinksUrls(urlMap, linksToRetrieve);

            const links: ResolvedFooterLink[] = updateFooterLinks.map((fl) => {
                // TODO: Remove this once the PWA Gift Cards is in production
                // https://dbuy-fi.atlassian.net/browse/SLTBE-5613
                if (!!pwaGiftCardsEnabled && fl.id === 5) {
                    return {
                        ...fl,
                        url: PageUrl.GIFT_CARDS,
                        isPwaLink: true,
                    };
                }
                return {
                    ...fl,
                    url: fl.url ?? resolvedLinksMap.get(fl.id) ?? '',
                    isPwaLink: false,
                };
            });
            return {
                title: messages.experienceTitle,
                links,
                containerDataTestId: 'footer-experience-links-container',
                headerDataTestId: 'footer-experience-links-header',
            };
        },
        retryOnMount: !allUrlsFound,
    });
}

const messages = defineMessages({
    experienceTitle: {
        id: 'footer.experience.link_title',
        defaultMessage: 'Sur La Table Experience',
    },
    cookingClassesTitle: {
        id: 'footer.experience.cooking_class_title',
        defaultMessage: 'Sur La Table Cooking Classes',
    },
    cookingClassesLabel: {
        id: 'footer.experience.cooking_class_label',
        defaultMessage: 'COOKING CLASSES',
    },
    storesTitle: {
        id: 'footer.experience.stores_title',
        defaultMessage: 'Find Your Store and see events',
    },
    storesLabel: {
        id: 'footer.experience.stores_label',
        defaultMessage: 'STORES',
    },
    sltPerksTitle: {
        id: 'footer.experience.stl_perks_title',
        defaultMessage: 'Our Rewards Program',
    },
    sltPerksLabel: {
        id: 'footer.experience.stl_perks_label',
        defaultMessage: 'SLT PERKS',
    },
    giftRegistryTitle: {
        id: 'footer.experience.gift_registry_title',
        defaultMessage: 'Find or manage a Gift Registry',
    },
    giftRegistryLabel: {
        id: 'footer.experience.gift_registry_label',
        defaultMessage: 'GIFT REGISTRY',
    },
    giftCardsTitle: {
        id: 'footer.experience.gift_cards_title',
        defaultMessage: 'Shop gift cards or check your balance',
    },
    giftCardsLabel: {
        id: 'footer.experience.gift_cards_label',
        defaultMessage: 'GIFT CARDS',
    },
    wishListTitle: {
        id: 'footer.experience.wish_list_title',
        defaultMessage: 'Make a Wish List',
    },
    wishListLabel: {
        id: 'footer.experience.wish_list_label',
        defaultMessage: 'WISH LIST',
    },
    recipesTitle: {
        id: 'footer.experience.recipes_title',
        defaultMessage: 'Browse Our Recipes',
    },
    recipesLabel: {
        id: 'footer.experience.recipes_label',
        defaultMessage: 'RECIPES',
    },
    cartTalkTitle: {
        id: 'footer.experience.card_talk_title',
        defaultMessage: 'See what people are buying',
    },
    cartTalkLabel: {
        id: 'footer.experience.card_talk_label',
        defaultMessage: 'CART TALK',
    },
    lidAndLadleTitle: {
        id: 'footer.experience.lid_and_ladle_title',
        defaultMessage: 'Lid & Ladle | Recipes & How-Tos',
    },
    lidAndLadleLabel: {
        id: 'footer.experience.lid_and_ladle_label',
        defaultMessage: 'LID & LADLE',
    },
    productCollectionsTitle: {
        id: 'footer.experience.product_collection_title',
        defaultMessage: 'Product Collections',
    },
    productCollectionsLabel: {
        id: 'footer.experience.product_collection_label',
        defaultMessage: 'PRODUCT COLLECTIONS',
    },
});

const footerLinkStores: FooterLink = {
    id: 2,
    title: messages.storesTitle,
    label: messages.storesLabel,
    dataTestId: 'footer.experience.stores',
};

const footerLinks: FooterLink[] = [
    {
        id: 1,
        title: messages.cookingClassesTitle,
        label: messages.cookingClassesLabel,
        dataTestId: 'footer.experience.cooking_class',
    },
    footerLinkStores,
    {
        id: 3,
        title: messages.sltPerksTitle,
        label: messages.sltPerksLabel,
        dataTestId: 'footer.experience.stl_perks',
    },
    {
        id: 4,
        title: messages.giftRegistryTitle,
        label: messages.giftRegistryLabel,
        dataTestId: 'footer.experience.gift_registry',
    },
    {
        id: 5,
        title: messages.giftCardsTitle,
        label: messages.giftCardsLabel,
        dataTestId: 'footer.experience.gift_cards',
    },
    {
        id: 6,
        title: messages.wishListTitle,
        label: messages.wishListLabel,
        dataTestId: 'footer.experience.wish_list',
    },
    {
        id: 7,
        title: messages.recipesTitle,
        label: messages.recipesLabel,
        dataTestId: 'footer.experience.recipes',
    },
    {
        id: 8,
        title: messages.cartTalkTitle,
        label: messages.cartTalkLabel,
        dataTestId: 'footer.experience.card_talk',
    },
    {
        id: 9,
        url: 'https://learn.surlatable.com/',
        title: messages.lidAndLadleTitle,
        label: messages.lidAndLadleLabel,
        showInNewTab: true,
        dataTestId: 'footer.experience.lid_and_ladle',
    },
    {
        id: 10,
        url: 'https://www.surlatable.com/o?c=pt-index',
        title: messages.productCollectionsTitle,
        label: messages.productCollectionsLabel,
        dataTestId: 'footer.experience.product_collection',
    },
];
